import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { MIN_REASON } from "./OverrideDialog"

/** General scope only (TUR-1573, design section 6): "declare on your own
 *  responsibility" for one or more contradicting frameworks at once. Mirrors
 *  `OverrideDialog.tsx`'s structure, validation (>= 10 chars) and pending
 *  state rather than inventing a new pattern. The caller owns the mutation
 *  and passes every framework key this confirm should acknowledge. */
export function GeneralAcknowledgeDialog({
  frameworkKeys,
  onConfirm,
  onClose,
  pending,
}: {
  frameworkKeys: string[]
  onConfirm: (reason: string) => void
  onClose: () => void
  pending: boolean
}) {
  const { translate } = useTranslation()
  const [reason, setReason] = useState("")
  const valid = reason.trim().length >= MIN_REASON

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_CS_CONF_V2_GENERAL_WARN_ACK")}
          </DialogTitle>
          <p
            data-testid="general-ack-body"
            className="text-[12.5px] leading-relaxed text-grey5"
          >
            {translate("MC_CS_CONF_V2_GENERAL_ACK_BODY")}
          </p>
          {/* An acknowledgement is the wrong tool when the hits DO concern the
              declared products: it is not printed, so the letter would still
              read as a clean statement. Name the disclosure path here, or the
              audited shortcut becomes the path of least resistance. */}
          <p
            data-testid="general-ack-disclosure-hint"
            className="text-[12px] leading-relaxed text-grey5"
          >
            {translate("MC_CS_CONF_V2_GENERAL_ACK_DISCLOSURE_HINT")}
          </p>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {frameworkKeys.map((key) => (
            <span
              key={key}
              className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11.5px] text-white"
            >
              {key}
            </span>
          ))}
        </div>
        <div>
          <label
            htmlFor="general-ack-reason"
            className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-widest text-grey5"
          >
            {translate("MC_CS_CONF_V2_GENERAL_ACK_REASON")}
          </label>
          <p className="mb-1.5 text-[11.5px] text-grey5">
            {translate("MC_CS_CONF_V2_GENERAL_ACK_REASON_HINT")}
          </p>
          <textarea
            id="general-ack-reason"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
          />
          <div
            className={cn(
              "mt-1 flex items-center justify-between text-[11px]",
              valid ? "text-grey5" : "text-warning",
            )}
          >
            <span>
              {valid
                ? translate("MC_CONF_V2_OVERRIDE_LOGGED")
                : translate("MC_CONF_V2_OVERRIDE_MIN")}
            </span>
            <span className="font-mono">
              {reason.trim().length} / {MIN_REASON}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={onClose}
          >
            {translate("MC_CS_CONF_EDIT_CANCEL")}
          </Button>
          <Button
            className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
            disabled={!valid || pending}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending && <Loader2 size={13} className="mr-1.5 animate-spin" />}
            {translate("MC_CS_CONF_V2_GENERAL_ACK_CONFIRM")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
